import { SubmissionParticipantForReception } from "@dune/types";
import {
	Checkbox,
	H3,
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { formatCurrency, traductActivities } from "@dune/utils";
import { useShouldDisplayHTAmount } from "../../hooks/useParticipantToSubmissionForm";
import { ParticipantDocumentsReception } from "../../pages/constructionSites/components/intervenants/ParticipantDocumentsReception";
import {
	BaseSubmissionParticipantTableProps,
	ParticipantsTableCell,
	ParticipantsTableHead,
} from "./participantsTable.helper";

interface MaterialsTableReceptionProps
	extends BaseSubmissionParticipantTableProps<SubmissionParticipantForReception> {
	onDocumentClick?: (file: {
		id: string;
		key: string;
		bucket: string;
		mimeType: string;
		name: string;
	}) => void;
	loadingDocumentId?: string | null;
}

export const MaterialsTableReception = ({
	submissionParticipants,
	constructionSiteId,
	selectedParticipantId,
	onParticipantSelect,
	onDocumentClick,
	loadingDocumentId,
}: MaterialsTableReceptionProps) => {
	const { shouldDisplayHT } = useShouldDisplayHTAmount(constructionSiteId);

	const totalAmount = submissionParticipants.reduce(
		(acc, submissionParticipant) => {
			const amount =
				shouldDisplayHT && submissionParticipant.totalFeesHT != null
					? submissionParticipant.totalFeesHT
					: submissionParticipant.totalFeesTTC;
			return acc + amount;
		},
		0,
	);

	return (
		<div className="mb-4">
			<H3>MATÉRIAUX</H3>
			<div className="bg-white rounded-lg border overflow-x-hidden">
				<Table className="table-fixed w-full overflow-x-auto">
					<TableHeader>
						<TableRow>
							<ParticipantsTableHead className="w-64">
								Type
							</ParticipantsTableHead>
							<ParticipantsTableHead className="w-48">
								Montant {shouldDisplayHT ? "(HT)" : "(TTC)"}
							</ParticipantsTableHead>
							<ParticipantsTableHead>
								Pièces justificatives
							</ParticipantsTableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{submissionParticipants.length > 0 ? (
							submissionParticipants.map((submissionParticipant) => {
								const materialTypes = traductActivities(
									submissionParticipant.totalMissionsOrActivitiesOrMaterialTypes,
								);
								const amount =
									shouldDisplayHT && submissionParticipant.totalFeesHT != null
										? submissionParticipant.totalFeesHT
										: submissionParticipant.totalFeesTTC;

								const isSelected =
									selectedParticipantId ===
									submissionParticipant.participant.id;

								return (
									<TableRow key={submissionParticipant.id}>
										<ParticipantsTableCell>
											<div className="flex items-center gap-4">
												<Checkbox
													id={`material-${submissionParticipant.id}`}
													checked={isSelected}
													onCheckedChange={(checked) => {
														onParticipantSelect(
															submissionParticipant.participant.id,
															checked === true,
														);
													}}
												/>
												<label
													htmlFor={`material-${submissionParticipant.id}`}
													className="truncate"
												>
													{materialTypes.join(", ")}
												</label>
											</div>
										</ParticipantsTableCell>
										<ParticipantsTableCell>
											{formatCurrency(amount)}
										</ParticipantsTableCell>
										<ParticipantsTableCell>
											<ParticipantDocumentsReception
												submissionParticipant={submissionParticipant}
												constructionSiteId={constructionSiteId}
												onDocumentClick={onDocumentClick}
												loadingDocumentId={loadingDocumentId}
											/>
										</ParticipantsTableCell>
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell
									colSpan={4}
									className="text-center text-gray-500 py-4"
								>
									Aucun matériaux
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				<div className="flex justify-end px-6 py-4 font-bold text-gray-700 border-t">
					Total matériaux
					<span className="ml-4 w-40 text-right">
						{formatCurrency(totalAmount)}
					</span>
				</div>
			</div>
		</div>
	);
};
